import { ChevronLeft, ChevronRight, Lock, LockOpen, Printer } from 'lucide-react';
import { orgMonth } from '@/lib/today';
import * as React from 'react';
import { atLeast, formatDuration, weeklyContractSeconds } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Combobox, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, toast,
} from '@/components/cargofl-ui';
import { toastFailure } from '@/lib/api';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { useVisiblePeople } from '@/lib/people-api';
import {
  monthReportUrl, useCloseMonth, useMonth, useReopenMonth, type MonthDay,
} from '@/lib/working-time-api';
import type { Session } from '@/lib/session';
import { usePeriodShortcuts } from '@/lib/shortcuts';

/**
 * The month: target hours against hours worked, day by day.
 *
 * A ledger rather than a dashboard. This is the view somebody opens because a
 * number looked wrong, so every day is a row whether or not anything was worked,
 * and the totals are the sum of the column above them.
 */
export function ReportsPage({ session }: { session: Session }) {
  const [month, setMonth] = React.useState(() => orgMonth());

  // Left/right or j/k to move a period, t for today. See lib/shortcuts.
  usePeriodShortcuts({
    previous: () => setMonth(shiftMonth(month, -1)),
    next: () => setMonth(shiftMonth(month, 1)),
    today: () => setMonth(orgMonth()),
  });
  const [employeeId, setEmployeeId] = React.useState<string>(session.id);
  // An employee reads their own month and nobody else's, so they are not offered
  // the list at all - and the option below still puts them in it.
  const { data: people } = useVisiblePeople(atLeast(session.role, 'SUPERVISOR'));
  const { data, isPending, isError, error } = useMonth(month, employeeId);
  const closeMonth = useCloseMonth();
  const reopenMonth = useReopenMonth();
  // HR and above only, mirroring the API - the button is a convenience, the
  // controller is the rule.
  const mayClose = atLeast(session.role, 'HR');

  const options = React.useMemo(() => {
    const rows = (people ?? []).map((p) => ({
      value: p.id,
      label: p.displayName,
      description: p.jobTitle ?? undefined,
    }));
    // The viewer is always offered, even when the people list is not available to
    // them - an employee can read their own month and nobody else's.
    if (!rows.some((r) => r.value === session.id)) {
      rows.unshift({ value: session.id, label: `${session.displayName} (me)`, description: undefined });
    }
    return rows;
  }, [people, session]);

  return (
    <PageContainer>
      <PageHeader
        title="Working time"
        description="Target hours against hours recorded, and the balance between them."
        actions={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" aria-label="Previous month" title="Previous month (← or j)"
              onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button hint={"Jump to the current month"} variant="outline" onClick={() => setMonth(orgMonth())}>
              This month
            </Button>
            <Button variant="outline" size="icon" aria-label="Next month" title="Next month (→ or k)"
              onClick={() => setMonth(shiftMonth(month, 1))}>
              <ChevronRight className="size-4" />
            </Button>
            {data?.contract && (
              /* A link, not a fetch: this is a document, and the browser's own print
                 dialog is what turns it into a PDF. */
              <Button hint={"Print this report, or save it as PDF"} asChild variant="outline" className="ml-2">
                <a href={monthReportUrl(month, employeeId)} target="_blank" rel="noreferrer">
                  <Printer className="size-4" />
                  Print
                </a>
              </Button>
            )}
          </div>
        }
      />

      {options.length > 1 && (
        <div className="max-w-xs">
          <Combobox
            value={employeeId}
            onChange={setEmployeeId}
            options={options}
            placeholder="Choose a person"
            searchPlaceholder="Search people"
          />
        </div>
      )}

      {/* Whether this month is closed, and the one action that changes it. A month
          closes as a deliberate act by a named person rather than as a side effect
          of whichever weeks happened to be submitted. */}
      {data?.contract && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-sm">
          {data.close.closed ? (
            <>
              <Lock className="size-4 text-muted-foreground" />
              <span>
                Closed by <strong>{data.close.closedBy}</strong> on{' '}
                {new Date(data.close.closedAt).toLocaleDateString()} at{' '}
                {formatDuration(data.close.actualSeconds)} of{' '}
                {formatDuration(data.close.expectedSeconds)}.
              </span>
              {mayClose && (
                <Button hint={"Reopen this month so its time can change again (asks for a reason)"}
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  disabled={reopenMonth.isPending}
                  onClick={() => {
                    const reason = window.prompt('Why is this month being reopened?');
                    if (reason === null) return;
                    // The reason is the audit trail's only record of why; a blank
                    // one used to be accepted and recorded as nothing.
                    if (!reason.trim()) {
                      toast.error('The month was not reopened: a reason is needed. Press Reopen and give one.');
                      return;
                    }
                    reopenMonth.mutate(
                      { employeeId, month, reason: reason.trim() },
                      { onError: toastFailure('Could not reopen the month') },
                    );
                  }}
                >
                  <LockOpen className="size-4" />
                  Reopen
                </Button>
              )}
            </>
          ) : (
            <>
              <LockOpen className="size-4 text-muted-foreground" />
              <span>
                {data.close.reopenedAt ? (
                  <>
                    Reopened by <strong>{data.close.reopenedBy}</strong> on{' '}
                    {new Date(data.close.reopenedAt).toLocaleDateString()}
                    {data.close.reopenReason ? ` — ${data.close.reopenReason}` : ''}.
                  </>
                ) : (
                  'This month is open.'
                )}
              </span>
              {mayClose && (
                <Button hint={"Lock this month — no more changes to its time"}
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  disabled={closeMonth.isPending}
                  onClick={() => closeMonth.mutate({ employeeId, month }, { onError: toastFailure('Could not close the month') })}
                >
                  <Lock className="size-4" />
                  Close this month
                </Button>
              )}
            </>
          )}
          {(closeMonth.error || reopenMonth.error) && (
            <span className="w-full text-destructive">
              {(closeMonth.error ?? reopenMonth.error)?.message}
            </span>
          )}
        </div>
      )}

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not load that month.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-72 w-full" />}

      {data && !data.contract && (
        <Alert>
          <AlertDescription className="text-sm leading-relaxed">
            {data.message ??
              'No working contract has been set up for this person, so there is no target to compare against.'}
          </AlertDescription>
        </Alert>
      )}

      {data?.contract && data.totals && (
        <>
          <Card>
            <CardContent className="flex flex-wrap gap-x-10 gap-y-4 py-4">
              {/* Leads with the to-date figures when the month is still running.
                  The full-month target against work so far reads as an enormous
                  shortfall on the 3rd - right, and useless. */}
              <Figure
                label={data.totals.toDate ? 'Target so far' : 'Target'}
                value={formatDuration(
                  (data.totals.toDate ?? data.totals).expectedSeconds,
                )}
              />
              <Figure
                label="Worked"
                value={formatDuration((data.totals.toDate ?? data.totals).actualSeconds)}
              />
              <Figure
                label={data.totals.toDate ? 'Balance so far' : 'Balance'}
                value={signed((data.totals.toDate ?? data.totals).balanceSeconds)}
                tone={
                  (data.totals.toDate ?? data.totals).balanceSeconds < 0
                    ? 'text-destructive'
                    : 'text-primary'
                }
              />
              {data.totals.toDate && (
                <Figure
                  label="Full month target"
                  value={formatDuration(data.totals.expectedSeconds)}
                />
              )}
              <Figure label="Working days" value={String(data.totals.workingDays)} />
              <Figure label="Absence" value={`${data.totals.absenceDays} days`} />
              <Figure label="Holidays" value={`${data.totals.holidayDays} days`} />
            </CardContent>
          </Card>

          <p className="text-xs leading-relaxed text-muted-foreground">
            Contracted {formatDuration(weeklyContractSeconds(data.contract.hoursPerDay))} per week.
            Absence is counted as{' '}
            {data.contract.absenceCalculationMode === 'REDUCE'
              ? 'a reduced target for the day'
              : 'time worked'}
            {data.contract.holidayGroup ? `, holidays from ${data.contract.holidayGroup.name}` : ''}.
          </p>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Worked</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.days.map((day) => (
                      <DayRow key={day.day} day={day} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`ts-num text-lg font-semibold ${tone ?? ''}`}>{value}</div>
    </div>
  );
}

function DayRow({ day }: { day: MonthDay }) {
  const date = new Date(`${day.day}T00:00:00.000Z`);
  const isWeekend = [0, 6].includes(date.getUTCDay());
  const label = date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  return (
    <TableRow className={isWeekend ? 'text-muted-foreground' : undefined}>
      <TableCell className="ts-num whitespace-nowrap">{label}</TableCell>
      <TableCell className="text-sm">
        {day.holidayName ? (
          <Badge variant="outline">{day.holidayName}</Badge>
        ) : day.note ? (
          <Badge variant="secondary">{day.note}</Badge>
        ) : null}
      </TableCell>
      <TableCell className="ts-num text-right">
        {day.expectedSeconds ? formatDuration(day.expectedSeconds) : <span className="opacity-40">—</span>}
      </TableCell>
      <TableCell className="ts-num text-right">
        {day.actualSeconds ? formatDuration(day.actualSeconds) : <span className="opacity-40">—</span>}
      </TableCell>
      <TableCell
        className={`ts-num text-right font-medium ${day.balanceSeconds < 0 ? 'text-destructive' : ''}`}
      >
        {signed(day.balanceSeconds)}
      </TableCell>
    </TableRow>
  );
}

/** A leading sign, so zero and a shortfall never look alike. */
function signed(seconds: number): string {
  if (seconds === 0) return '0:00';
  return `${seconds < 0 ? '−' : '+'}${formatDuration(Math.abs(seconds))}`;
}

function shiftMonth(month: string, delta: number): string {
  const [year, m] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year!, m! - 1 + delta, 1));
  return date.toISOString().slice(0, 7);
}
